import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { ArrowLeft, Clock, Calendar, User, Share2, Facebook, Twitter, Linkedin, Loader2 } from 'lucide-react';
import JourneyHeader from './JourneyHeader';
import Footer from './Footer';
import { API_BASE_URL } from '../config/api';
import { resolveDayTheme } from '../data/dayThemes';

const API_URL = API_BASE_URL;

// Related Articles Component
const RelatedArticles = ({ currentSlug, category }) => {
  const [related, setRelated] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchRelated = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/articles`);
        const articles = await response.json();
        const filtered = articles
          .filter(a => a.slug !== currentSlug && a.published)
          .slice(0, 3);
        setRelated(filtered);
      } catch (e) { /* silent */ }
    };
    fetchRelated();
  }, [currentSlug, category]);

  if (related.length === 0) return null;

  return (
    <div className="mt-12">
      <h3 className="text-2xl font-light text-white text-center mb-6">Continue the Journey</h3>
      <div className="grid md:grid-cols-3 gap-4">
        {related.map((a) => (
          <button
            key={a.slug}
            onClick={() => { navigate(`/article/${a.slug}`); window.scrollTo(0, 0); }}
            className="bg-white/5 backdrop-blur-sm rounded-xl overflow-hidden border border-white/10 hover:border-yellow-400/30 transition-all text-left group"
          >
            <img
              src={a.image || a.image_url || 'https://images.unsplash.com/photo-1499209974431-9dddcece7f88?w=400&h=200&fit=crop'}
              alt={a.title}
              className="w-full h-32 object-cover group-hover:scale-105 transition-transform duration-500"
            />
            <div className="p-4">
              <p className="text-white text-sm font-medium line-clamp-2 group-hover:text-yellow-400 transition-colors">
                {a.title}
              </p>
              <p className="text-gray-500 text-xs mt-2">{a.readTime || a.read_time || '5 min read'}</p>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

// Process and clean article content for better display
const processArticleContent = (content, featuredImage) => {
  if (!content) return '';
  
  let processed = content;
  
  // Remove the first image in the body if it matches the featured/hero image
  // This prevents the duplicate artwork at top + body
  if (featuredImage) {
    // Extract just the filename/key part for comparison (handles URL variations)
    const getFuzzyKey = (url) => {
      if (!url) return '';
      try {
        return decodeURIComponent(url).split('/').pop().split('?')[0].toLowerCase();
      } catch (e) {
        return url.split('/').pop().split('?')[0].toLowerCase();
      }
    };
    const featuredKey = getFuzzyKey(featuredImage);
    
    // Find and remove the first <img> in the content if its src matches the featured image
    const firstImgMatch = processed.match(/<img[^>]+src=["']([^"']+)["'][^>]*>/i);
    if (firstImgMatch) {
      const bodyImgKey = getFuzzyKey(firstImgMatch[1]);
      if (bodyImgKey === featuredKey) {
        // Remove the first matching image (and its wrapping figure/div/p if present)
        processed = processed.replace(
          new RegExp(`(<figure[^>]*>\\s*)?(<p[^>]*>\\s*)?(<div[^>]*>\\s*)?${firstImgMatch[0].replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(\\s*<\\/div>)?(\\s*<\\/p>)?(\\s*<figcaption[^>]*>.*?<\\/figcaption>\\s*<\\/figure>)?`, 'i'),
          ''
        );
      }
    }
  }
  
  // Remove duplicate title (h1 at the start that matches article title)
  processed = processed.replace(/^<h1[^>]*>.*?<\/h1>\s*/i, '');
  
  // Remove "Report this article" noise from LinkedIn scrapes
  processed = processed.replace(/<li[^>]*>Report this article<\/li>/gi, '');
  
  // Remove author name headings that duplicate metadata
  processed = processed.replace(/<h3[^>]*>Cherise T\.<\/h3>/gi, '');
  
  // Clean up empty paragraphs
  processed = processed.replace(/<p[^>]*>\s*<\/p>/gi, '');
  
  return processed;
};

const ArticlePage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [showDownloadForm, setShowDownloadForm] = useState(false);
  const [dlForm, setDlForm] = useState({ name: '', email: '', phone: '' });
  const [dlSubmitting, setDlSubmitting] = useState(false);
  const [downloadSuccess, setDownloadSuccess] = useState(false);

  const handleDownloadRequest = async (e) => {
    e.preventDefault();
    setDlSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/download/request-epiic-hero`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(dlForm)
      });
      if (response.ok) {
        setDownloadSuccess(true);
      } else {
        alert('Something went wrong. Please try again.');
      }
    } catch (err) {
      alert('Connection error. Please try again.');
    } finally {
      setDlSubmitting(false);
    }
  };

  // Track scroll progress
  useEffect(() => {
    const handleScroll = () => {
      const totalHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = totalHeight > 0 ? (window.scrollY / totalHeight) * 100 : 0;
      setScrollProgress(progress);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Hardcoded articles for backward compatibility
  const hardcodedArticles = {
    '1010-portal-divine-alignment': {
      title: 'The 1010 Portal: Opening Doors to Divine Alignment',
      excerpt: 'Discover how the sacred 1010 portal can transform your spiritual journey and align you with divine purpose',
      category: 'Spirituality',
      author: 'Keeper Of The Fiyah',
      created_at: '2025-01-20',
      read_time: '8 min read',
      image_url: 'https://images.unsplash.com/photo-1528715471579-d1bcf0ba5e83?w=1200&h=600&fit=crop',
      content: `
        <h2>Understanding the 1010 Portal</h2>
        <p>The 1010 portal is a powerful spiritual gateway that opens when you repeatedly encounter the numbers 10:10. This isn't mere coincidence—it's a divine message from the universe, calling you to awaken to your higher purpose and align with your soul's mission.</p>
        <p>When you see 10:10 on your clock, receipts, phone notifications, or anywhere else, pay attention. The universe is trying to communicate with you, offering guidance and support on your spiritual journey.</p>
        <h2>YAH HEALTH WEALTH HAPPINESS Through 1010</h2>
        <p>The 1010 portal aligns perfectly with the universal pillars of <strong>YAH HEALTH WEALTH HAPPINESS</strong>.</p>
      `
    }
  };

  const fetchArticle = useCallback(async () => {
    setLoading(true);
    try {
      // Try to fetch from database first (public published-only endpoint)
      const response = await fetch(`${API_URL}/api/articles`);
      const articles = await response.json();
      
      // Find article by slug
      const foundArticle = articles.find(a => a.slug === slug);
      
      if (foundArticle) {
        setArticle(foundArticle);
      } else {
        // Check hardcoded articles as fallback
        const hardcodedArticle = hardcodedArticles[slug];
        if (hardcodedArticle) {
          setArticle(hardcodedArticle);
        } else {
          setError('Article not found');
        }
      }
    } catch (err) {
      console.error('Error fetching article:', err);
      // Fallback to hardcoded articles
      const hardcodedArticle = hardcodedArticles[slug];
      if (hardcodedArticle) {
        setArticle(hardcodedArticle);
      } else {
        setError('Article not found');
      }
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    fetchArticle();
  }, [fetchArticle]);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(to bottom, #403B4A 0%, #E7E9BB 50%, #403B4A 100%)' }}>
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-purple-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading article...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !article) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(to bottom, #403B4A 0%, #E7E9BB 50%, #403B4A 100%)' }}>
        <div className="text-center">
          <h1 className="text-4xl font-bold mb-4 text-white">Article Not Found</h1>
          <p className="text-gray-300 mb-6">The article you're looking for doesn't exist or has been removed.</p>
          <button 
            onClick={() => navigate('/')} 
            className="px-6 py-3 bg-purple-600 text-white rounded-full hover:bg-purple-700 transition-colors"
          >
            Return Home
          </button>
        </div>
      </div>
    );
  }

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return 'Unknown date';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  // Calculate read time (rough estimate: 200 words per minute)
  const calculateReadTime = (content) => {
    if (!content) return '5 min read';
    const wordCount = content.replace(/<[^>]*>/g, '').split(/\s+/).length;
    const minutes = Math.ceil(wordCount / 200);
    return `${minutes} min read`;
  };

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(to bottom, #1a1a2e 0%, #16213e 50%, #1a1a2e 100%)' }}>
      <JourneyHeader activeSection="journey" />

      {/* Hero Section */}
      <div className="relative pt-20" style={{ minHeight: '600px' }}>
        {/* Dark background base */}
        <div className="absolute inset-0 bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900"></div>
        
        {/* Hero Image - Centered and contained */}
        <div className="relative z-10 flex justify-center pt-8 pb-4">
          <div className="relative w-full max-w-2xl mx-auto px-6">
            <img 
              src={article.image_url || article.image || 'https://images.unsplash.com/photo-1499209974431-9dddcece7f88?w=1200&h=600&fit=crop'}
              alt={article.title}
              className="w-full h-auto max-h-[350px] object-contain rounded-lg shadow-2xl"
              style={{ filter: 'drop-shadow(0 0 30px rgba(139, 92, 246, 0.3))' }}
            />
          </div>
        </div>
        
        {/* Back Button — gated during Phase 1 to keep visitors on the article */}
        <button
          onClick={() => navigate(-1)}
          className="absolute top-24 left-6 lg:left-12 z-20 flex items-center space-x-2 px-4 py-2 bg-white/20 backdrop-blur-sm text-white rounded-full hover:bg-white/30 transition-all"
        >
          <ArrowLeft size={20} />
          <span>Back</span>
        </button>

        {/* Article Header - Below the image */}
        <div className="relative z-10 px-6 lg:px-12 pb-12 pt-6">
          <div className="max-w-4xl mx-auto text-center">
            <div className="flex flex-wrap items-center justify-center gap-2 mb-4">
              <span className="inline-block px-4 py-2 bg-purple-600 rounded-full text-white text-sm font-semibold">
                {article.category || 'INSPIYAH'}
              </span>
              {(() => {
                const dt = resolveDayTheme(article.dailyTheme || article.daily_theme);
                if (!dt) return null;
                return (
                  <span
                    data-testid="day-theme-ribbon"
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r ${dt.gradient} ${dt.text} text-sm font-semibold shadow-lg ring-1 ring-white/15`}
                  >
                    <span className="font-bold tracking-wide">{dt.displayName}</span>
                    <span className="opacity-70">—</span>
                    <span className="opacity-90 font-normal">{dt.theme}</span>
                  </span>
                );
              })()}
            </div>
            <h1 className="text-4xl lg:text-5xl font-bold text-white mb-4">
              {article.title}
            </h1>
            {article.excerpt && (
              <p className="text-lg text-white/80 mb-6 max-w-2xl mx-auto">
                {article.excerpt}
              </p>
            )}
            <div className="flex flex-wrap items-center justify-center gap-4 text-white/70 text-sm">
              <div className="flex items-center space-x-2">
                <User size={16} />
                <span>{article.author || 'Keeper Of The Fiyah'}</span>
              </div>
              {/* Original date from source */}
              {article.original_date && (
                <div className="flex items-center space-x-2">
                  <Calendar size={16} />
                  <span>Originally: {article.original_date}</span>
                </div>
              )}
              {/* Ecosystem date (when added to INSPIYAH) */}
              <div className="flex items-center space-x-2">
                <Calendar size={16} />
                <span>{article.ecosystem_date ? `Added: ${article.ecosystem_date}` : formatDate(article.created_at || article.date)}</span>
              </div>
              <div className="flex items-center space-x-2">
                <Clock size={16} />
                <span>{article.read_time || article.readTime || calculateReadTime(article.content)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Article Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Reading Progress Bar */}
        <div className="fixed top-0 left-0 right-0 z-50 h-1 bg-gray-200">
          <div 
            className="h-full bg-gradient-to-r from-purple-600 via-orange-500 to-yellow-500 transition-all duration-150"
            style={{ width: `${scrollProgress}%` }}
          />
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8 lg:p-12" style={{ backgroundColor: '#ffffff' }}>
          {/* Article Body */}
          <style>{`
            .article-body h1 { font-size: 2.25rem; font-weight: 700; color: #111827 !important; margin: 2.5rem 0 1rem; letter-spacing: -0.02em; }
            .article-body h2 { font-size: 1.875rem; font-weight: 700; color: #111827 !important; margin: 2.5rem 0 1rem; letter-spacing: -0.01em; }
            .article-body h3 { font-size: 1.5rem; font-weight: 600; color: #1f2937 !important; margin: 2rem 0 0.75rem; }
            .article-body p { color: #1f2937 !important; margin-bottom: 1.25rem; line-height: 1.9; font-size: 1.125rem; }
            .article-body li { color: #1f2937 !important; margin-bottom: 0.5rem; margin-left: 1.5rem; list-style-type: disc; font-size: 1.125rem; line-height: 1.8; }
            .article-body ul, .article-body ol { margin: 1rem 0; padding-left: 1.5rem; color: #1f2937 !important; }
            .article-body blockquote { 
              border-left: 4px solid #d4a853; 
              padding: 1.5rem 2rem; 
              margin: 2rem 0;
              background: linear-gradient(135deg, #fefce8 0%, #faf5ff 100%);
              border-radius: 0 0.75rem 0.75rem 0;
              font-style: italic;
              color: #4b5563 !important;
              font-size: 1.1rem;
              line-height: 1.8;
              position: relative;
            }
            .article-body blockquote::before {
              content: '\\201C';
              position: absolute;
              top: -10px;
              left: 12px;
              font-size: 4rem;
              color: #d4a853;
              opacity: 0.3;
              font-family: Georgia, serif;
            }
            .article-body strong { font-weight: 700; color: #111827 !important; }
            .article-body em { font-style: italic; color: #374151 !important; }
            .article-body a { color: #7c3aed !important; text-decoration: underline; }
            .article-body a:hover { color: #5b21b6 !important; }
            .article-body img { 
              max-width: 100%; 
              height: auto; 
              border-radius: 0.75rem; 
              box-shadow: 0 10px 25px -5px rgba(0,0,0,0.15);
              margin: 2.5rem auto;
              display: block;
            }
            .article-body figure { margin: 2.5rem 0; }
            .article-body figcaption { text-align: center; color: #6b7280 !important; font-size: 0.875rem; margin-top: 0.75rem; font-style: italic; }
            .article-body hr {
              border: none;
              height: 1px;
              background: linear-gradient(90deg, transparent, #d4a853, transparent);
              margin: 3rem 0;
            }
            /* Section dividers between template sections */
            .article-body > div + div {
              margin-top: 2.5rem;
              padding-top: 2.5rem;
              border-top: 1px solid #f3f4f6;
            }
            /* Pull quote styling for declarations */
            .article-body .kotf-closing,
            .article-body .abracadabra-section,
            .article-body .yhwh-vows-section,
            .article-body .ancient-principles-protection {
              margin: 3rem 0;
              padding: 2rem;
              background: linear-gradient(135deg, #1a0533 0%, #2d1b4e 100%);
              border-radius: 1rem;
              color: #e5e7eb !important;
              border: 1px solid rgba(212, 168, 83, 0.3);
            }
            .article-body .kotf-closing p,
            .article-body .abracadabra-section p,
            .article-body .yhwh-vows-section p,
            .article-body .ancient-principles-protection p {
              color: #e5e7eb !important;
            }
            .article-body .kotf-closing h3,
            .article-body .abracadabra-section h3,
            .article-body .yhwh-vows-section h3,
            .article-body .ancient-principles-protection h3 {
              color: #fbbf24 !important;
              font-size: 1.25rem;
            }
            .article-body .kotf-closing strong,
            .article-body .abracadabra-section strong,
            .article-body .yhwh-vows-section strong,
            .article-body .ancient-principles-protection strong {
              color: #fbbf24 !important;
            }
            .article-body .kotf-closing em,
            .article-body .abracadabra-section em,
            .article-body .yhwh-vows-section em,
            .article-body .ancient-principles-protection em {
              color: #c084fc !important;
            }
            .article-body .kotf-closing blockquote,
            .article-body .abracadabra-section blockquote,
            .article-body .yhwh-vows-section blockquote,
            .article-body .ancient-principles-protection blockquote {
              background: rgba(212, 168, 83, 0.1);
              border-left-color: #d4a853;
              color: #e5e7eb !important;
            }
            .article-body .inspiyah-culture-section {
              margin: 2.5rem 0;
              padding: 2.5rem;
              background: linear-gradient(135deg, #0f172a 0%, #1e1b4b 100%);
              border-radius: 1rem;
              border: 1px solid rgba(139, 92, 246, 0.3);
              text-align: center;
            }
            .article-body .inspiyah-culture-section p {
              color: #ffffff !important;
              font-size: 1.1rem;
            }
            .article-body .inspiyah-culture-section h3 {
              color: #a78bfa !important;
              font-size: 1.4rem;
              letter-spacing: 0.05em;
            }
            .article-body .inspiyah-culture-section em {
              color: #d1d5db !important;
              font-size: 1.05rem;
            }
            .article-body .inspiyah-culture-section strong {
              color: #fbbf24 !important;
              font-size: 1.1rem;
            }
            .article-body .inspiyah-culture-section a {
              color: #fbbf24 !important;
              font-weight: 600;
              font-size: 1rem;
            }
            /* General header section */
            .article-body .general-header {
              margin-bottom: 2.5rem;
              padding-bottom: 2rem;
              border-bottom: 2px solid #f3f4f6;
            }
          `}</style>
          <div 
            className="article-body"
            style={{ color: '#1f2937' }}
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(processArticleContent(article.content, article.image_url || article.image)) }}
          />

          {/* Subscribe CTA — EPIIC Hero Kit Download */}
          <div className="mt-10 p-8 bg-gradient-to-r from-purple-900 via-indigo-900 to-orange-900 rounded-xl border border-yellow-400/20 text-center">
            <p className="text-yellow-400 text-xs uppercase tracking-widest mb-2">Join the World of INSPIYAH</p>
            <h3 className="text-2xl font-light text-white mb-3">Get Your Free EPIIC Hero Kit</h3>
            <p className="text-gray-300 text-sm mb-5 max-w-md mx-auto">
              "Comedy Hour: A Nation's Prayer — Fiyah Edition" — a 25-page visual experience delivered to your inbox.
            </p>
            <button
              onClick={() => setShowDownloadForm(true)}
              className="inline-block px-8 py-3 bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 text-black font-semibold rounded-full hover:scale-105 transition-transform shadow-lg"
              data-testid="article-download-cta"
            >
              Claim Your Free Download
            </button>
          </div>

          {/* Inline Download Form Modal */}
          {showDownloadForm && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" onClick={(e) => { if (e.target === e.currentTarget) setShowDownloadForm(false); }}>
              <div className="bg-gradient-to-b from-gray-900 to-black rounded-2xl p-8 max-w-md w-full mx-4 border border-yellow-400/20 shadow-2xl relative">
                <button onClick={() => setShowDownloadForm(false)} className="absolute top-4 right-4 text-gray-400 hover:text-white text-xl">&times;</button>
                
                {!downloadSuccess ? (
                  <>
                    <div className="text-center mb-6">
                      <p className="text-yellow-400 text-xs uppercase tracking-widest mb-2">EPIIC Hero Kit</p>
                      <h3 className="text-xl font-light text-white">Enter your details</h3>
                    </div>
                    <form onSubmit={handleDownloadRequest} className="space-y-4">
                      <input
                        type="text"
                        placeholder="Your Name"
                        value={dlForm.name}
                        onChange={(e) => setDlForm(prev => ({ ...prev, name: e.target.value }))}
                        required
                        className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-yellow-400"
                      />
                      <input
                        type="email"
                        placeholder="Your Email"
                        value={dlForm.email}
                        onChange={(e) => setDlForm(prev => ({ ...prev, email: e.target.value }))}
                        required
                        className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-yellow-400"
                      />
                      <input
                        type="tel"
                        placeholder="Your Phone"
                        value={dlForm.phone}
                        onChange={(e) => setDlForm(prev => ({ ...prev, phone: e.target.value }))}
                        required
                        className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-yellow-400"
                      />
                      <button
                        type="submit"
                        disabled={dlSubmitting}
                        className="w-full py-3 bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 text-black font-semibold rounded-full hover:scale-105 transition-transform disabled:opacity-50"
                      >
                        {dlSubmitting ? 'Sending...' : 'Send My Download'}
                      </button>
                    </form>
                  </>
                ) : (
                  <div className="text-center py-4">
                    <div className="inline-block p-3 bg-green-500/20 rounded-full mb-3">
                      <svg className="w-8 h-8 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                    </div>
                    <h3 className="text-xl font-light text-white mb-2">Check Your Email!</h3>
                    <p className="text-gray-300 text-sm">Download link sent to <strong className="text-yellow-400">{dlForm.email}</strong></p>
                    <p className="text-gray-500 text-xs mt-2">Link expires in 48 hours.</p>
                    <button onClick={() => { setShowDownloadForm(false); setDownloadSuccess(false); setDlForm({ name: '', email: '', phone: '' }); }} className="mt-4 px-6 py-2 bg-white/10 text-white rounded-full text-sm hover:bg-white/20 transition-colors">Close</button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Reaction Buttons */}
          <div className="mt-8 pt-6 border-t border-gray-200">
            <p className="text-gray-500 text-sm text-center mb-4">How did this article make you feel?</p>
            <div className="flex items-center justify-center space-x-4">
              {[
                { emoji: '\uD83D\uDD25', label: 'Fire', id: 'fire' },
                { emoji: '\u2764\uFE0F', label: 'Love', id: 'love' },
                { emoji: '\uD83D\uDCA1', label: 'Wisdom', id: 'wisdom' },
                { emoji: '\uD83D\uDE4F', label: 'Inspiyahed', id: 'blessed' },
              ].map((reaction) => (
                <button
                  key={reaction.id}
                  onClick={async () => {
                    try {
                      await fetch(`${API_URL}/api/articles/${article.slug || slug}/react`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ reaction: reaction.id })
                      });
                    } catch (e) { /* silent */ }
                    const el = document.getElementById(`reaction-${reaction.id}`);
                    if (el) { el.classList.add('scale-125'); setTimeout(() => el.classList.remove('scale-125'), 300); }
                  }}
                  id={`reaction-${reaction.id}`}
                  className="flex flex-col items-center px-5 py-3 bg-gray-50 hover:bg-purple-50 rounded-xl transition-all duration-200 hover:scale-110"
                  data-testid={`reaction-${reaction.id}`}
                >
                  <span className="text-2xl mb-1">{reaction.emoji}</span>
                  <span className="text-xs text-gray-500">{reaction.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Share Section */}
          <div className="mt-8 pt-8 border-t border-gray-200">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center space-x-2 text-gray-600">
                <Share2 size={20} />
                <span className="font-semibold">Share this article:</span>
              </div>
              <div className="flex items-center space-x-4">
                <a 
                  href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-3 bg-blue-700 text-white rounded-full hover:bg-blue-800 transition-colors"
                >
                  <Linkedin size={20} />
                </a>
                <a
                  href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-3 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors"
                >
                  <Facebook size={20} />
                </a>
                <a
                  href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent(article.title)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-3 bg-sky-500 text-white rounded-full hover:bg-sky-600 transition-colors"
                >
                  <Twitter size={20} />
                </a>
              </div>
            </div>
          </div>

          {/* Cite This Article */}
          <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <p className="text-xs text-gray-500 font-medium mb-2">Cite this article:</p>
            <p className="text-xs text-gray-600 font-mono select-all">
              {article.author || 'Keeper Of The Fiyah'}. "{article.title}." ART Energi / World of INSPIYAH, {article.date || new Date().getFullYear()}. {typeof window !== 'undefined' ? window.location.href : ''}
            </p>
          </div>

          {/* Author Bio */}
          <div className="mt-8 p-6 bg-gradient-to-r from-purple-50 to-orange-50 rounded-xl">
            <div className="flex items-start space-x-4">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-orange-500 rounded-full flex items-center justify-center text-white font-bold text-xl">
                {(article.author || 'K')[0]}
              </div>
              <div>
                <h4 className="font-bold text-gray-900">{article.author || 'Keeper Of The Fiyah'}</h4>
                <p className="text-gray-600 text-sm mt-1">
                  Guiding souls on the INSPIYAH Journey through divine wisdom, spiritual insight, and the power of YAHFFIRMATIONS.
                </p>
                <p className="text-purple-600 text-sm mt-2 font-medium">
                  YAH HEALTH WEALTH HAPPINESS - The Universal Pillars Of Success
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Related Articles */}
        <RelatedArticles currentSlug={slug} category={article.category} />

        {/* Back to Journey Button */}
        <div className="text-center mt-12">
          <button
            onClick={() => navigate('/journey')}
            className="px-8 py-4 bg-gradient-to-r from-purple-600 to-orange-500 text-white rounded-full font-semibold hover:shadow-lg transition-all"
          >
            Explore More on the INSPIYAH Journey
          </button>
        </div>
      </div>

      {/* SEO Structured Data for LLM/Search readability */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Article",
        "headline": article.title,
        "description": article.excerpt || '',
        "author": {
          "@type": "Person",
          "name": article.author || "Keeper Of The Fiyah",
          "url": "https://artenergi.org"
        },
        "publisher": {
          "@type": "Organization",
          "name": "ART Energi / World of INSPIYAH",
          "url": "https://artenergi.org"
        },
        "datePublished": article.original_date || article.date || article.created_at,
        "dateModified": article.updated_at || article.date,
        "image": article.image || article.image_url,
        "keywords": "YHWH יהוה, Yahweh, Jehovah, YAHFFIRMATIONS, spirituality, faith, wellness, INSPIYAH, Keeper of the Fiyah, YAH HEALTH WEALTH HAPPINESS, divine intelligence, ancient principles",
        "articleSection": article.category || "Spirituality",
        "inLanguage": "en"
      }) }} />

      <Footer />
    </div>
  );
};

export default ArticlePage;
